import {
  type ChangeEvent,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import classes from "~/features/scanDmc/components/FileUpload/FileUpload.module.less";
import { IconSize } from "~/common/components/icon/IconSize";
import { Trans, useTranslation } from "react-i18next";
import classNames from "classnames";
import { Link, LinkButton } from "~/common/components/Link";
import { SpeechBubble } from "~/features/scanDmc/components/SpeechBubble/SpeechBubble";
import { uniqueValues } from "~/common/utils/misc";
import { CircularProgress } from "~/common/mui";
import { Alert } from "~/common/mui/Alert/Alert";

interface FileUploadProps {
  className?: string;
  maxSize?: number;
  name?: string;
  onChange?: (files: File[] | null) => void;
  value?: File[] | null;
  disabled?: boolean;
  error?: ReactNode;
  // accept?: string;
  multiple?: boolean;
}

const makeSetMultipleRefs =
  <T,>(...refs: ForwardedRef<T>[]) =>
  (el: T | null) => {
    refs.forEach((ref) => {
      if (ref) {
        if (typeof ref === "function") {
          ref(el);
        } else {
          ref.current = el;
        }
      }
    });
  };

const syncFiles = (files: File[] | null, ref: HTMLInputElement | null) => {
  if (!ref) {
    return;
  }
  const fileTransfer = new DataTransfer();
  files?.forEach((file) => {
    fileTransfer.items.add(file);
  });
  ref.files = fileTransfer.files;
};

export const FileUpload = forwardRef<HTMLInputElement, FileUploadProps>(
  ({ className, maxSize = 10000000, value: externalValue, onChange, disabled, error, ...rest }, ref) => {
    // TODO - Decompose this into a compound component, at that point "accept" can be passed in as a prop;
    const accept = ".pdf, .jpg, .jpeg, .png";
    // TODO - Get validation outside?
    // TODO - Make error more generic?

    const { t } = useTranslation();
    const internalRef = useRef<HTMLInputElement | null>(null);
    const setRefs = makeSetMultipleRefs(ref, internalRef);

    const [isControlled] = useState(() => externalValue !== undefined);
    const [internalValue, setInternalValue] = useState<File[] | null | undefined>(externalValue);
    const value = (isControlled ? externalValue : internalValue) ?? [];

    const onChangeCb = (e: ChangeEvent<HTMLInputElement>) => {
      let newValue;
      if (!e.target.files) {
        newValue = null;
      } else if (getOverrideFiles(e.nativeEvent)) {
        // This is a hack to make sure that the files are not appended to the existing ones
        newValue = Array.from(e.target.files ?? []);
      } else {
        newValue = [...value, ...Array.from(e.target.files ?? [])];
        syncFiles(newValue, internalRef.current);
      }
      setInternalValue(newValue);
      onChange?.(newValue);
    };

    const openFileBrowser = () => {
      internalRef?.current?.click();
    };

    const removeFilesFromState = (index: number) => {
      const fileTransfer = new DataTransfer();
      value.filter((_, i) => i !== index).forEach((file) => fileTransfer.items.add(file));

      if (internalRef.current) {
        internalRef.current.files = fileTransfer.files;
        const e = new Event("change", { bubbles: true });
        setOverrideFiles(e);
        internalRef.current.dispatchEvent(e);
      }
    };

    return (
      <div className={classNames(classes.root, className)}>
        <input hidden type="file" ref={setRefs} onChange={onChangeCb} disabled={disabled} accept={accept} {...rest} />
        <LinkButton
          type={"button"}
          className={classes.addLink}
          disabled={disabled}
          onClick={openFileBrowser}
          iconBefore={<Icon size={IconSize.FONT_16} type={IconType.FilePlus} />}
        >
          {t("page.scandmc.form.uploadFile.button.attachFile")}
        </LinkButton>

        {error}

        {value.length > 0 && (
          <div className={classes.fileList}>
            {value.map((file, index) => (
              <UploadedItem key={file.name} file={file} onRemove={() => removeFilesFromState(index)} />
            ))}
          </div>
        )}

        <FileUploadTooltip />
      </div>
    );
  },
);

const usePreviewImage = (file: File) => {
  const [previewComponent, setPreviewComponent] = useState<ReactNode>(null);
  useLayoutEffect(() => {
    if (file.type.includes("image")) {
      const url = URL.createObjectURL(file);
      setPreviewComponent(<img width={40} height={40} src={url} />);

      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewComponent(<Icon size={40} type={IconType.Pdf} />);
    }
  }, []);
  return previewComponent;
};

interface UploadedItemProps {
  file: File;
  onRemove: () => void;
}

const UploadedItem = ({ file, onRemove }: UploadedItemProps) => {
  const { t } = useTranslation();
  const previewComponent = usePreviewImage(file);

  return (
    <div className={classes.item}>
      <div className={classes.itemImage}>{previewComponent}</div>
      <div className={classes.itemAttached}>{t("page.scandmc.form.uploadFile.label.attached")}</div>
      <div className={classes.itemName}>
        <span>{file.name}</span>
        <span> - {parseByteValues(file.size)}</span>
      </div>

      <LinkButton className={classes.itemRemove} onClick={onRemove}>
        <Icon size={12} type={IconType.Close} />
      </LinkButton>
    </div>
  );
};

function parseByteValues(bytes: number) {
  if (bytes > MB_DIVIDER) {
    return `${(bytes / MB_DIVIDER).toFixed(1)} MB`;
  }
  return `${(bytes / KB_DIVIDER).toFixed(1)} KB`;
}

const MB_DIVIDER = 1_000_000;
const KB_DIVIDER = 1_000;

const FileUploadTooltip = () => {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);

  const currentLanguage = window.SCHA.config.language.toUpperCase();
  useEffect(() => {
    (async () => {
      setUrl(undefined);
      setLoading(true);
      const AVAILABLE_LANGUAGES = uniqueValues([currentLanguage, "EN"]);
      for (const lang of AVAILABLE_LANGUAGES) {
        const url = makeUrl(lang);
        const exists = await checkIfFileExists(url);
        if (exists) {
          setUrl(url);
          break;
        }
      }
      setLoading(false);
    })();
  }, [currentLanguage]);

  const icon = loading ? <CircularProgress size={16} /> : <Icon size={16} type={IconType.Newspaper} />;
  const disableLink = loading || !url;
  const hideLink = !loading && !url;

  return (
    <SpeechBubble>
      <div className={classes.instructions}>
        <p>
          <Trans i18nKey={"feature.scandmc.fileUpload.instructions"} />
        </p>
        {!hideLink && (
          <Link iconBefore={icon} disabled={disableLink} href={url} target={"_blank"}>
            {t("feature.scandmc.fileUpload.openGuideBtn")}
          </Link>
        )}
      </div>
    </SpeechBubble>
  );
};

const checkIfFileExists = async (url: string) => {
  try {
    const response = await fetch(url, { method: "HEAD" });
    return response.ok;
  } catch (e) {
    return false;
  }
};

const makeUrl = (lang: string) =>
  `https://cdn.schaeffler-ecommerce.com/frontend/One_Code_Photo_Guideline/Photo_Guideline_One_Code_${lang.toUpperCase()}.pdf`;

export const MaxSizeError = ({ maxSize }: { maxSize: number }) => {
  const { t } = useTranslation();
  return (
    <Alert className={classes.maxSizeError} severity="error">
      {t("page.scandmc.form.uploadFile.error.maxSize", { max: parseByteValues(maxSize) })}
    </Alert>
  );
};

export const countTotalSize = (files: File[]) => {
  return files.reduce((acc, file) => acc + file.size, 0);
};

function setOverrideFiles(event: Event) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (event as any)["_overrideFiles"] = true;
}

function getOverrideFiles(event: Event) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (event as any)["_overrideFiles"];
}
